import WatchedCampaignActions from './WatchedCampaignActions';
import SourcesLoading, { SourcesProgress } from './SourcesLoading';
import React, { useEffect, useMemo, useState } from 'react';
import {
  campaignErrorMessage,
  connectChatGptAds,
  disconnectConnection,
  getConnection,
  importCampaigns,
  listChatGptAdsCampaigns,
  listImportedCampaigns,
  stopWatchingCampaign,
} from '../api/adpilotApi';

const CAMPAIGN_LIMIT = 20;

function ChatGptAdsConnectCard({ user }) {
  const [connection, setConnection] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [available, setAvailable] = useState([]);
  const [imported, setImported] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showImporter, setShowImporter] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!user.preview);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const userId = user.id;

  const owner = !connection?.access_role || connection.access_role === 'owner';
  const active = connection?.status === 'active' && Boolean(connection.ad_account_id);
  const watched = useMemo(() => imported.filter((campaign) => campaign.watched !== false), [imported]);
  const unwatched = useMemo(() => imported.filter((campaign) => campaign.watched === false), [imported]);
  const watchedIds = useMemo(() => new Set(watched.map((campaign) => String(campaign.platform_campaign_id))), [watched]);
  const slots = Math.max(0, CAMPAIGN_LIMIT - watched.length);

  const refreshCampaigns = async (current) => {
    const [catalog, live] = await Promise.all([
      listImportedCampaigns({ userId, connectionId: current.connection_id, scope: 'all' }),
      listChatGptAdsCampaigns({ userId, connectionId: current.connection_id }),
    ]);
    setImported(Array.isArray(catalog) ? catalog : []);
    setAvailable(Array.isArray(live) ? live : []);
  };

  useEffect(() => {
    if (user.preview) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const current = (await getConnection({ userId, provider: 'chatgpt-ads' })).connection;
        if (cancelled) return;
        setConnection(current);
        if (current?.status === 'active' && current.ad_account_id) await refreshCampaigns(current);
      } catch (loadError) {
        if (!cancelled && loadError?.response?.status !== 404) {
          setError(campaignErrorMessage(loadError, 'Failed to load your ChatGPT Ads connection'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, user.preview]);

  const handleConnect = async (event) => {
    event.preventDefault();
    if (busy) return;
    setLoading(true);
    setBusy(true); setError(null); setMessage(null);
    try {
      const result = await connectChatGptAds({ userId, apiKey });
      setApiKey('');
      setConnection(result.connection);
      await refreshCampaigns(result.connection);
      setMessage('ChatGPT Ads connected. Choose the campaigns AdPilot should watch.');
    } catch (connectError) {
      setError(campaignErrorMessage(connectError, 'Could not connect ChatGPT Ads'));
    } finally { setBusy(false); setLoading(false); }
  };

  const handleDisconnect = async () => {
    if (busy || !window.confirm('Disconnect ChatGPT Ads from AdPilot?\n\nAnalysis stops immediately. Imported campaigns, briefs, and history are kept.')) return;
    setBusy(true); setError(null); setMessage(null);
    try {
      await disconnectConnection({ userId, provider: 'chatgpt-ads' });
      setConnection(null); setAvailable([]); setImported([]); setSelectedIds(new Set());
      setMessage('ChatGPT Ads disconnected. Your history is kept — reconnect any time.');
    } catch (disconnectError) {
      setError(campaignErrorMessage(disconnectError, 'Could not disconnect ChatGPT Ads'));
    } finally { setBusy(false); }
  };

  const refresh = () => refreshCampaigns(connection);
  const watchCampaigns = async (campaignIds) => {
    if (!campaignIds.length || busy) return;
    setBusy(true); setError(null);
    try {
      await importCampaigns({ userId, connectionId: connection.connection_id, campaignIds });
      setSelectedIds(new Set()); setShowImporter(false); await refresh();
    } catch (watchError) { setError(campaignErrorMessage(watchError, 'Could not update monitoring')); }
    finally { setBusy(false); }
  };
  const importAll = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      const result = await importCampaigns({ userId, connectionId: connection.connection_id, all: true });
      await refresh();
      setMessage(`Imported ${result.synced} campaign${result.synced === 1 ? '' : 's'}. Choose up to ${CAMPAIGN_LIMIT} to watch.`);
    } catch (importError) { setError(campaignErrorMessage(importError, 'Could not import campaigns')); }
    finally { setBusy(false); }
  };
  const unwatch = async (id) => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      await stopWatchingCampaign({ userId, connectionId: connection.connection_id, platformCampaignId: id });
      await refresh();
    } catch (unwatchError) { setError(campaignErrorMessage(unwatchError, 'Could not stop watching that campaign')); }
    finally { setBusy(false); }
  };
  const toggle = (id) => setSelectedIds((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else if (next.size < slots) next.add(id);
    return next;
  });

  if (user.preview) return null;
  if (loading) return <SourcesLoading platform="ChatGPT Ads" />;
  const unimportedCount = available.filter((campaign) => !imported.some((row) => String(row.platform_campaign_id) === String(campaign.platform_campaign_id))).length;

  return (
    <>
      {busy && <SourcesProgress platform="ChatGPT Ads" updating />}
      {active && (
        <div className="connection-summary" aria-label="ChatGPT Ads connection status">
          <span className="summary-pill">ChatGPT Ads connected</span>
          <span className="summary-pill">{connection.ad_account_id}</span>
          {watched.length > 0 && <span className="summary-pill">{watched.length} campaign{watched.length === 1 ? '' : 's'} watched</span>}
          {unwatched.length > 0 && <span className="summary-pill">{imported.length} imported</span>}
          {owner && <button type="button" className="summary-action" disabled={busy} onClick={handleDisconnect}>Disconnect</button>}
        </div>
      )}
      {error && <div className="notice notice-error" role="alert">{error}</div>}
      {message && <div className="notice" role="status">{message}</div>}
      <section className="card" aria-label="ChatGPT Ads connection">
        {!active ? (
          owner ? (
            <form className="chatgpt-connect-form" onSubmit={handleConnect}>
              <h2 className="card-title">Connect your ChatGPT Ads account</h2>
              <p className="card-copy chatgpt-connect-copy">Connect with an account-scoped API key from ChatGPT Ads Manager. AdPilot validates it with ChatGPT Ads and stores it encrypted.</p>
              <ol className="card-copy chatgpt-connect-instructions" aria-label="How to connect ChatGPT Ads">
                <li>Open ChatGPT Ads Manager settings and create an account-scoped API key.</li>
                <li>Paste the key below and connect your account.</li>
                <li>Choose which campaigns AdPilot should monitor.</li>
              </ol>

              <div className="builder-field chatgpt-connect-field">
                <label htmlFor="chatgpt-ads-api-key">API key</label>
                <input id="chatgpt-ads-api-key" type="password" autoComplete="off" aria-describedby="chatgpt-ads-api-key-help" value={apiKey} onChange={(event) => setApiKey(event.target.value)} placeholder="Paste your account-scoped API key" />
                <small id="chatgpt-ads-api-key-help">Your key is stored encrypted and is never displayed again after you connect.</small>
              </div>

              <div className="campaign-actions">
                <button type="submit" className="btn btn-primary" disabled={busy || apiKey.trim().length < 20}>{busy ? 'Connecting…' : 'Connect ChatGPT Ads'}</button>
                <a className="btn btn-secondary" href="https://ads.openai.com/" target="_blank" rel="noopener noreferrer">Open Ads Manager</a>
              </div>
            </form>
          ) : <p className="card-copy">The workspace owner manages the ChatGPT Ads connection.</p>
        ) : (
          <>
            <div className="section-heading">
              <div><h2>{watched.length ? 'Campaigns AdPilot watches' : 'Choose your campaigns'}</h2><span>{watched.length ? `${watched.length} of ${CAMPAIGN_LIMIT} monitoring slots used.` : `Watch up to ${CAMPAIGN_LIMIT}. Choose the campaigns AdPilot should analyze.`}</span></div>
              {watched.length > 0 && <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setShowImporter((value) => !value)}>{showImporter ? 'Done' : 'Manage monitoring'}</button>}
            </div>
            {(watched.length === 0 || showImporter) && (
              <>
                {available.length === 0 && !error && <p className="card-copy">No campaigns are visible on this ChatGPT Ads account yet.</p>}
                {available.length > 0 && <div className="campaign-actions"><button type="button" className="btn btn-primary" disabled={busy || selectedIds.size === 0} onClick={() => watchCampaigns([...selectedIds])}>Watch selected ({selectedIds.size})</button>{owner && unimportedCount > 0 && <button type="button" className="btn btn-secondary" disabled={busy} onClick={importAll}>Import all {available.length} without watching</button>}</div>}
                <div className="campaign-grid" aria-label="Available ChatGPT Ads campaigns">
                  {available.map((campaign) => {
                    const isWatched = watchedIds.has(String(campaign.platform_campaign_id));
                    return <label key={campaign.platform_campaign_id} className="campaign-option"><input type="checkbox" checked={isWatched || selectedIds.has(campaign.platform_campaign_id)} disabled={busy || (!isWatched && !selectedIds.has(campaign.platform_campaign_id) && selectedIds.size >= slots)} onChange={() => (isWatched ? unwatch(campaign.platform_campaign_id) : toggle(campaign.platform_campaign_id))} /><span className="campaign-option-copy"><strong>{campaign.name}</strong><small>{campaign.status || 'ChatGPT Ads campaign'}</small></span></label>;
                  })}
                </div>
              </>
            )}
            {watched.length > 0 && !showImporter && <div className="synced-list" aria-label="Watched ChatGPT Ads campaigns">{watched.map((campaign) => <div key={campaign.platform_campaign_id} className="synced-row"><strong>{campaign.name || campaign.platform_campaign_id}</strong><span>{campaign.status || 'Synced'}</span><span>{campaign.last_sync_at || campaign.imported_at ? `Updated ${new Date(campaign.last_sync_at || campaign.imported_at).toLocaleDateString()}` : 'Synced'}</span><WatchedCampaignActions campaign={campaign} connection={connection} user={user} disabled={busy}
                      onChanged={(updated) => setImported((current) => current.map((item) => String(item.platform_campaign_id) === String(updated.platform_campaign_id) ? updated : item))}
                      onUnwatch={() => unwatch(campaign.platform_campaign_id)} /></div>)}</div>}
            {unwatched.length > 0 && !showImporter && <><div className="section-heading"><div><h2>Imported, not watched</h2><span>Synced from ChatGPT Ads and ready to watch when a slot is free. AdPilot does not analyze these.</span></div></div><div className="synced-list">{unwatched.map((campaign) => <div key={campaign.platform_campaign_id} className="synced-row"><strong>{campaign.name || campaign.platform_campaign_id}</strong><span>{campaign.status || 'Synced'}</span><span>{campaign.last_sync_at || campaign.imported_at ? `Updated ${new Date(campaign.last_sync_at || campaign.imported_at).toLocaleDateString()}` : 'Synced'}</span><button type="button" className="text-button" disabled={busy || slots === 0} onClick={() => watchCampaigns([campaign.platform_campaign_id])}>{slots === 0 ? 'No slots free' : 'Watch'}</button></div>)}</div></>}
          </>
        )}
      </section>
    </>
  );
}

export default ChatGptAdsConnectCard;
